"use client";

import { useChatDock } from "@/components/ChatDock";

import { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { usePathname } from "next/navigation";
import { thetaApi, dataOf } from "@/lib/api/theta";
import type { components } from "@theta/api-client";
import { isThetaError } from "@theta/api-client";
import { uploadFile, type Upload } from "@/lib/api/uploads";
import { Dialog, DialogHeader, DialogTitle } from "./ui/Dialog";
import { ConfirmDialog } from "./ui/ConfirmDialog";

type Mode = "text" | "voice";
type RecordState = "idle" | "recording" | "recorded";

function fmt(s: number) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

interface FeedbackValue {
  /** Open the feedback modal from anywhere — the mobile menu uses this. */
  openFeedback: () => void;
  /** False on /login and for signed-out visitors, where feedback is hidden. */
  feedbackAvailable: boolean;
}

const FeedbackContext = createContext<FeedbackValue | null>(null);

export function useFeedback(): FeedbackValue {
  return (
    useContext(FeedbackContext) ?? { openFeedback: () => {}, feedbackAvailable: false }
  );
}

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const { occupiedHeight } = useChatDock();
  const { data: user } = useCurrentUser();
  const pathname = usePathname();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("text");
  const [text, setText] = useState("");
  /**
   * THE UPLOAD ID IS WHAT GETS SENT; THE URL IS ONLY FOR THE THUMBNAIL.
   *
   * `POST /feedback` takes `imageUploadIds` and `voiceUploadId`, not URLs
   * (§1.11) — each is checked to be owned by the submitter and scoped
   * `feedback`, so the row has an ACL before anything points at it. It also
   * removes a real hazard the old payload had: a media URL is signed and
   * hour-rounded, so a URL persisted into a Linear issue expires, while an
   * upload id does not.
   */
  const [images, setImages] = useState<{ id: Upload["id"]; url: string }[]>([]);
  const [uploadingImages, setUploadingImages] = useState(0);
  const [recordState, setRecordState] = useState<RecordState>("idle");
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * THE RESET FENCES ITS STRAGGLERS. `handleClose` resets synchronous state,
   * but three continuations can still be in flight when it runs: an image
   * `uploadFile` promise, `MediaRecorder.onstop`, and the submit POST. Left
   * unfenced they land ON the reset provider — the upload re-adds its ghost
   * image and its `finally` decrements the counter to -1 (which disables
   * "Send feedback" forever, since this provider never unmounts), `onstop`
   * resurrects the discarded recording, and a late `setDone` greets the next
   * opening with a thank-you for feedback that was never typed. Each
   * continuation captures this generation when it starts and drops its state
   * writes if a close has bumped it since.
   */
  const closeGenRef = useRef(0);

  const handleClose = useCallback(() => {
    closeGenRef.current += 1;
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setOpen(false);
    setText("");
    setMode("text");
    setRecordState("idle");
    // Revoke, or every discarded recording leaks its blob URL for the life of
    // the page. Inside the updater because this callback is identity-stable
    // (`[]`) and would otherwise close over a stale URL; revoking an
    // already-revoked URL is a no-op, so a double-invoked updater is harmless.
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setAudioBlob(null);
    setSeconds(0);
    setSubmitting(false);
    setDone(false);
    setError(null);
    setImages([]);
    setUploadingImages(0);
    setConfirmDiscard(false);
  }, []);

  /**
   * A DISMISSAL THAT WOULD LOSE WORK ASKS FIRST (OSO-679, same rule as
   * EventComposer's OSO-404). Escape, the backdrop and the ✕ all land on
   * `requestClose`: instant for an empty modal, a `ConfirmDialog` when it
   * holds unsent content — `window.confirm` is ratcheted out (ratchets.json).
   * After `done` the feedback IS sent, so nothing is lost by closing.
   * NOT `useDraft`: the ticket asks for a warning, not persistence.
   */
  const isDirty =
    !done &&
    (text.trim().length > 0 ||
      images.length > 0 ||
      uploadingImages > 0 ||
      recordState !== "idle");

  // A plain function, deliberately: `useCallback` bought nothing here — its
  // deps churn on every keystroke, and `useModalSurface` reads `onClose`
  // through a ref, so a fresh identity never re-registers the escape layer.
  function requestClose() {
    // A POST in flight is neither "sent" nor safely discardable, so the
    // confirm's wording would be wrong either way; sending takes a beat, and
    // the close waits for it.
    if (submitting) return;
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    handleClose();
  }

  const available = Boolean(user) && !pathname?.startsWith("/login");

  // Session expiry (the polled /me) or landing on /login while the modal is
  // open unmounts both dialogs WITHOUT unmounting this provider: nothing
  // stopped the mic stream (the browser's recording indicator stayed on) and
  // `open`/`confirmDiscard` survived, so both dialogs popped back the moment
  // `available` returned. The full close, not just `setOpen(false)`, is the
  // cleanup that covers both.
  useEffect(() => {
    if (!available && open) handleClose();
  }, [available, open, handleClose]);

  async function uploadImage(file: File) {
    const gen = closeGenRef.current;
    setUploadingImages(n => n + 1);
    try {
      const result = await uploadFile(file, {
        scope: { type: "feedback" },
        fallbackMessage: "Kunne ikke uploade billede. Prøv igen.",
      });
      if (gen !== closeGenRef.current) return;
      setImages(prev => [...prev, { id: result.id, url: result.url }]);
    } catch (e) {
      if (gen !== closeGenRef.current) return;
      setError(e instanceof Error ? e.message : "Kunne ikke uploade billede. Prøv igen.");
    } finally {
      // The close already zeroed the counter; decrementing past it would pin
      // `canSubmit`'s `uploadingImages === 0` false for good.
      if (gen === closeGenRef.current) setUploadingImages(n => n - 1);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const imageItems = Array.from(e.clipboardData.items).filter(i => i.type.startsWith("image/"));
    if (imageItems.length === 0) return;
    e.preventDefault();
    imageItems.forEach(item => { const f = item.getAsFile(); if (f) uploadImage(f); });
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    Array.from(e.target.files ?? []).forEach(uploadImage);
    e.target.value = "";
  }

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/ogg";
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      const gen = closeGenRef.current;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        // The hardware cleanup runs unconditionally; a close mid-recording
        // has already done it, and these are no-ops then.
        if (timerRef.current) clearInterval(timerRef.current);
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        // `stop()` from `handleClose` lands here AFTER the reset — without
        // the fence it resurrected the discarded recording as fresh state.
        if (gen !== closeGenRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setRecordState("recorded");
      };

      recorder.start();
      setRecordState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch {
      setError("Kunne ikke få adgang til mikrofonen. Tjek dine browserindstillinger.");
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
  }

  function resetRecording() {
    setRecordState("idle");
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setAudioBlob(null);
    setSeconds(0);
  }

  async function handleSubmit() {
    const gen = closeGenRef.current;
    setSubmitting(true);
    setError(null);
    try {
      let body: components["schemas"]["CreateFeedbackInput"];

      if (mode === "text") {
        body = { message: text.trim() || null, imageUploadIds: images.map(i => i.id) };
      } else if (audioBlob) {
        const ext = audioBlob.type.includes("ogg") ? "ogg" : "webm";
        const upload = await uploadFile(
          new File([audioBlob], `feedback.${ext}`, { type: audioBlob.type }),
          { scope: { type: "feedback" }, fallbackMessage: "Upload af stemmebesked fejlede" }
        );
        body = { voiceUploadId: upload.id };
      } else {
        body = {};
      }

      // `Idempotency-Key` is REQUIRED here (D13 names `feedback` explicitly)
      // and the client mints one per POST.
      dataOf<components["schemas"]["FeedbackReceipt"]>(
        await thetaApi.http.POST("/feedback", { body }),
      );
      if (gen !== closeGenRef.current) return;
      setDone(true);
    } catch (e) {
      if (gen !== closeGenRef.current) return;
      if (isThetaError(e, "bodyEmpty")) {
        setError("Skriv en besked eller vedhæft noget, før du sender.");
      } else if (isThetaError(e, "attachmentNotOwned")) {
        setError("En af vedhæftningerne kunne ikke bruges. Prøv at uploade den igen.");
      } else if (isThetaError(e, "rateLimited")) {
        setError("For mange indsendelser. Vent et øjeblik og prøv igen.");
      } else {
        setError(e instanceof Error ? e.message : "Kunne ikke sende feedback. Prøv igen.");
      }
    } finally {
      if (gen === closeGenRef.current) setSubmitting(false);
    }
  }

  const canSubmit = !submitting && uploadingImages === 0 && (
    mode === "text"
      ? text.trim().length >= 3 || images.length > 0
      : recordState === "recorded"
  );

  const contextValue = useMemo(
    () => ({ openFeedback: () => setOpen(true), feedbackAvailable: available }),
    [available],
  );

  return (
    <FeedbackContext.Provider value={contextValue}>
      {children}

      {/* Floating feedback button — desktop only. On small screens it covered
          whatever sat in the bottom-right corner, so there it lives in the
          mobile menu instead (see SiteHeader). */}
      {/* Lifted clear of any docked chat windows, which share this corner. */}
      {available && (
      <button
        onClick={() => setOpen(true)}
        aria-label="Giv feedback"
        style={occupiedHeight ? { bottom: occupiedHeight + 24 } : undefined}
        className="hidden lg:flex fixed bottom-6 right-6 z-40 items-center gap-2 px-4 py-2 rounded-full bg-surface border border-border shadow-md hover:shadow-lg hover:border-indigo-300 dark:hover:border-indigo-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
        Feedback
      </button>
      )}

      {available && (
        <>
        <Dialog open={open} onClose={requestClose}>
          <DialogHeader className="flex items-center justify-between">
            <div>
              <DialogTitle>Giv feedback</DialogTitle>
              <p className="text-xs text-subtle-foreground mt-0.5">Theta er under udvikling — din mening tæller</p>
            </div>
            <button
              onClick={requestClose}
              aria-label="Luk"
              className="p-2 -m-2 text-subtle-foreground hover:text-foreground rounded-control hover:bg-muted transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </DialogHeader>

            <div className="px-6 py-5 space-y-4">
              {done ? (
                <div className="text-center py-6">
                  <div className="text-4xl mb-3">🙏</div>
                  <p className="font-semibold text-foreground">Tak for din feedback!</p>
                  <p className="text-sm text-muted-foreground mt-1">Vi bruger den til at gøre Theta bedre.</p>
                  <button
                    onClick={handleClose}
                    className="mt-5 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors"
                  >
                    Luk
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex rounded-xl bg-muted p-1 gap-1">
                    {(["text", "voice"] as const).map((m) => (
                      <button
                        key={m}
                        onClick={() => { setMode(m); setError(null); }}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                          mode === m
                            ? "bg-white dark:bg-gray-700 text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                      >
                        {m === "text" ? "Tekst" : "Stemme"}
                      </button>
                    ))}
                  </div>

                  {mode === "text" ? (
                    <div className="space-y-2">
                      <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onPaste={handlePaste}
                        placeholder="Hvad synes du om Theta indtil videre?"
                        rows={4}
                        autoFocus
                        className="w-full px-4 py-3 text-sm bg-surface-2 border border-border rounded-xl text-foreground placeholder-subtle-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                      />

                      {(images.length > 0 || uploadingImages > 0) && (
                        <div className="flex flex-wrap gap-2">
                          {images.map((img, i) => (
                            <div key={i} className="relative group">
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img src={img.url} alt="" className="h-16 w-16 object-cover rounded-lg border border-border" />
                              <button
                                onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-800 dark:bg-gray-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs leading-none"
                              >×</button>
                            </div>
                          ))}
                          {Array.from({ length: uploadingImages }).map((_, i) => (
                            <div key={`uploading-${i}`} className="h-16 w-16 rounded-lg border border-border bg-muted flex items-center justify-center">
                              <svg className="animate-spin w-4 h-4 text-subtle-foreground" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                              </svg>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-1.5 text-xs text-subtle-foreground hover:text-foreground transition-colors"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                          Tilføj billede
                        </button>
                        <span className="text-xs text-subtle-foreground">· eller indsæt med Ctrl+V</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handleFileChange}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3 py-4">
                      {recordState === "idle" && (
                        <>
                          <button
                            onClick={startRecording}
                            className="w-20 h-20 rounded-full bg-primary-muted/40 hover:bg-indigo-200 dark:hover:bg-indigo-800/60 flex items-center justify-center transition-colors"
                          >
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                              <line x1="12" y1="19" x2="12" y2="23" />
                              <line x1="8" y1="23" x2="16" y2="23" />
                            </svg>
                          </button>
                          <p className="text-sm text-muted-foreground">Tryk for at optage</p>
                        </>
                      )}

                      {recordState === "recording" && (
                        <>
                          <button
                            onClick={stopRecording}
                            className="w-20 h-20 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors animate-pulse"
                          >
                            <span className="w-8 h-8 rounded-md bg-white" />
                          </button>
                          <p className="text-lg font-mono tabular-nums text-gray-800 dark:text-gray-200">{fmt(seconds)}</p>
                          <p className="text-xs text-muted-foreground">Optager… tryk for at stoppe</p>
                        </>
                      )}

                      {recordState === "recorded" && audioUrl && (
                        <>
                          <audio src={audioUrl} controls className="w-full rounded-lg" />
                          <button
                            onClick={resetRecording}
                            className="text-xs text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                          >
                            Optag igen
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {error && (
                    <p className="text-sm text-destructive bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 rounded-xl px-4 py-3">
                      {error}
                    </p>
                  )}

                  <button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className="w-full py-3 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                  >
                    {submitting ? "Sender…" : uploadingImages > 0 ? "Uploader…" : "Send feedback"}
                  </button>
                </>
              )}
            </div>
        </Dialog>

        {/* A sibling AFTER the Dialog, like EventComposer's: opened later, so
            the escape-layer registry (registration order, last first) hands
            Escape to the confirm — one press closes it, not both dialogs. */}
        <ConfirmDialog
          open={confirmDiscard}
          title="Kassér feedback?"
          description="Din feedback er ikke sendt endnu. Hvis du lukker nu, mister du den."
          confirmLabel="Kassér"
          cancelLabel="Skriv videre"
          // `handleClose` resets every field, so the next opening starts blank.
          onConfirm={handleClose}
          onCancel={() => setConfirmDiscard(false)}
        />
        </>
      )}
    </FeedbackContext.Provider>
  );
}
